import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { HelpHint } from '@/components/common/help/HelpHint'
import { HelpText } from '@/components/common/help/HelpText'
import { helpKey } from '@/lib/action-modules/help'
import { DayOfWeek, type ScheduleDay } from '@/lib/action-modules/registry/scheduling/types'

export interface ScheduleDaysDefinitionProps {
  dayModel: ScheduleDay[]
  onUpdateDayModel: (value: ScheduleDay[]) => void
}

const ALL_DAYS: DayOfWeek[] = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
]

export function ScheduleDaysDefinitionEditor(props: ScheduleDaysDefinitionProps) {
  const { t } = useTranslation('action-modules-wizard')
  const sd = (key: string) => t(`schedulingStrategy.scheduleDefinition.${key}`)
  const toggleDay = (dayOfWeek: DayOfWeek, checked: boolean) => {
    if (checked) {
      props.onUpdateDayModel([...props.dayModel, { dayOfWeek, startTime: '00:00', endTime: '23:59' }])
    } else {
      props.onUpdateDayModel(props.dayModel.filter(d => d.dayOfWeek !== dayOfWeek))
    }
  }

  const updateDay = (existing: ScheduleDay, patch: Partial<ScheduleDay>) => {
    props.onUpdateDayModel(props.dayModel.map(d => (d === existing ? { ...d, ...patch } : d)))
  }

  return (
    <div className="flex flex-col gap-2 pt-4">
      <h4 className="text-sm font-semibold">{sd('days.title')}</h4>
      <HelpText helpKey={helpKey('schedulingDays')} />
      {/* Column headers for the time inputs, which otherwise carry only an aria-label. One
          shared header for all seven rows keeps the help to a single trigger. */}
      <div className="flex flex-wrap items-center gap-3 pt-1">
        <div className="min-w-40 flex-1" />
        <span className="w-32 text-xs font-medium text-muted-foreground">{sd('days.start')}</span>
        <span className="w-32 text-xs font-medium text-muted-foreground">{sd('days.end')}</span>
        {/* Fixed-size slot so the columns stay aligned with the rows below even when the
            hint renders nothing because its text has not been written yet. */}
        <div className="size-4 shrink-0">
          <HelpHint helpKey={helpKey('schedulingDaysTimeRange')} />
        </div>
      </div>
      {ALL_DAYS.map(day => {
        const model = props.dayModel.find(d => d.dayOfWeek === day)
        const checked = !!model
        return (
          <div key={day} className="flex flex-wrap items-center gap-3">
            <Label className="flex min-w-40 flex-1 items-center gap-2 font-normal">
              <Checkbox checked={checked} onCheckedChange={c => toggleDay(day, c === true)} />
              {t(`days.${day}`)}
            </Label>
            <Input
              type="time"
              className="w-32"
              disabled={!checked}
              value={model?.startTime ?? ''}
              onChange={e => model && updateDay(model, { startTime: e.target.value || null })}
              aria-label={sd('days.start')}
            />
            <Input
              type="time"
              className="w-32"
              disabled={!checked}
              value={model?.endTime ?? ''}
              onChange={e => model && updateDay(model, { endTime: e.target.value || null })}
              aria-label={sd('days.end')}
            />
            {/* Spacer matching the header's help trigger so the columns stay aligned. */}
            <div className="size-4 shrink-0" />
          </div>
        )
      })}
    </div>
  )
}
